<template>
    <v-card>
        <v-card-title class="accent white--text subheading">
            {{'Supervised Detection Scores (Higher indicates error)'|translate}}
        </v-card-title>
        <v-card-text>
            <loading-progress-bar :loading="loading"></loading-progress-bar>
            <vue-plotly :data="mydata" :layout="layout" :options="options" @click="handleClick"></vue-plotly>
            <v-layout row wrap v-if="showDetails">
                <v-flex xs12 >
                    <v-card-title class="accent white--text subheading">
                        {{selectedModel.model_name}}
                    </v-card-title>
                    <div class="pa-3">
                        <p v-if="selectedModel.severity"><strong>{{'Severity'|translate}} : </strong>{{selectedModel.severity}}</p>
                        <p v-if="selectedModel.recommended_actions"><strong>{{'Recommended actions'|translate}} : </strong></p>
                        <ul v-if="selectedModel.recommended_actions">
                            <li v-for="i in selectedModel.recommended_actions.split('\n')">{{i}}</li>
                        </ul>
                    </div>
                </v-flex>
                <v-flex xs12 v-if="fullVariableList">
                    <data3-chart v-for="(unitVars,unitName) in fullVariableList" :key="unitName"
                                 :layout-overrides="{height:300,margin:layoutOverrides.margin,shapes:layoutOverrides.shapes,yaxis:{title:unitName,fixedrange: true},showlegend: true,legend:{...layoutOverrides.legend},xaxis:{...layoutOverrides.xaxis},hovermode:layoutOverrides.hovermode,hoverlabel:layoutOverrides.hoverlabel}"
                                 :on-only="false"
                                 :x-axis="'timestamp'"
                                 ref="plots"
                                 just-plot
                                 disable-responsive
                                 :show-export="false"
                                 :data-payload="unitVars"
                                 :date-range="range"></data3-chart>
                </v-flex>
                <v-flex xs12 v-if="compPlots">
                    <data3-chart ref="plots" v-for="plot in compPlots" v-if="plot.type==='data3-chart'"
                                 :layout-overrides="plot.config['layout-overrides']"
                                 :x-axis="plot.config['x-axis']"
                                 :y-axis="plot.config['y-axis']"
                                 disable-responsive
                                 show-units
                                 :data-payload="plot.config['data-payload']"
                                 :series-overrides="plot.config['series-overrides']"
                                 :date-range="range"></data3-chart>
                </v-flex>
            </v-layout>
        </v-card-text>
    </v-card>
</template>

<script>
    import {getSupervisedResults, getModel} from '@/helper'
    import VuePlotly from '@/components/VuePlotly'
    import LoadingProgressBar from '../LoadingProgressBar'
    import _ from 'lodash'
    import Data3Chart from "../Data3Chart";
    import {addMinutes, compareAsc} from 'date-fns'

    export default {
        name: 'SupervisedSummary',
        components: {LoadingProgressBar, VuePlotly,Data3Chart},
        props: {
            range: Object,
            equipment: String
        },
        data() {
            return {
                loading: true,
                showDetails: false,
                fullVariableList:null,
                layoutOverrides:null,
                selectedModel:null,
                compPlots:null,
                df: null,
                layout: {
                    autosize: true,
                    timeout: null,
                    hovermode: 'closest',
                    height: 360,
                    margin: {
                        l: 400,
                        r: 50,
                        b: 50,
                        t: 30,
                        pad: 4
                    },
                    yaxis: {
                        title: this.$options.filters.translate('Health'),
                    },
                    xaxis: {
                        title: this.$options.filters.translate('Time')
                    }
                },
                options: {},
                mydata: [],
                mydata2: [],
                layout2: {
                    autosize: true,
                    hovermode: 'closest',
                    yaxis: {
                        title: this.$options.filters.translate('Sensor')
                    },
                    xaxis: {
                        title: this.$options.filters.translate('Sensor')
                    },
                    title: {text: ''}
                },
                currentTime: null,
                models: []
            }
        },
        watch: {
            range() {
                this.refresh()
            },
            equipment() {
                this.refresh()
            }
        },
        computed: {
            facilityName() {
                return this.$store.state.pointsListStore.pointsList.facility_name
            },
            modelDict() {
                let output = {}
                for (let i in this.models) {
                    output[this.models[i]['model_id']] = this.models[i]
                }
                return output
            }
        },
        methods: {
            debounce(callback, wait) {
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => callback(), wait);
            },
            refresh() {
                this.debounce(this.doRefresh, 200);
            },
            async doRefresh() {
                this.loading = true
                this.showDetails = false
                this.models = (await getModel(
                    {'facility_name': this.facilityName}
                ))['result']

                let df = await getSupervisedResults(
                    this.equipment,
                    this.facilityName,
                    {'start': this.range.start, 'end': this.range.end}
                )
                this.$emit('vischange', df.timestamp.length > 0 || df.model_id.length > 0)

                let currentTime = _.min(df['timestamp'])
                let endTime = _.max(df['timestamp'])
                while (compareAsc(currentTime, endTime) < 0) {
                    currentTime = addMinutes(currentTime, this.$store.getters.minutesBetweenSamples)
                    df['value'].push(null)
                    df['model_id'].push(-1)
                    df['timestamp'].push(currentTime)
                }
                let self = this
                let colorscaleValue = [
                    [0, process.env.VUE_APP_THEME == 'dark' ? "rgb(31, 119, 180)" : '#0000FF'],
                    [1, process.env.VUE_APP_THEME == 'dark' ? "rgb(214, 39, 40)" : '#FF0000']
                ]
                this.mydata = [{
                    x: df['timestamp'],
                    y: _.map(df['model_id'], function (id) {
                        if (id == -1) return ' '
                        return id + '__' + self.modelDict[id]['model_name']
                    }),
                    z: df['value'],
                    type: 'heatmap',
                    mode: 'markers',
                    colorscale: colorscaleValue,
                    zmin: 0,
                    zmax: 1
                }]
                this.loading = false
            },
            getUnit(varName){
                for (let point of this.$store.state.pointsListStore.pointsList.points_list){
                    if(point.sensor_name===varName&&point.equipment_name===this.equipment){
                        if(point.unit){return point.unit}
                        break
                    }
                }
                if(varName.toLowerCase().includes("tem")){
                    return "°C"
                } else if(varName.toLowerCase().includes("press")){
                    return "kPa"
                } else if(varName.toLowerCase().includes("status")){
                    return "status"
                } else if(varName.toLowerCase().includes("_hr")){
                    return "hour"
                }
                return "";
            },
            handleClick(data) {
                this.showDetails=false
                if(data.points[0].pointIndex){
                    let selectedTitle = data.points[0].y
                    let selectedModelId = selectedTitle.split('__')[0]
                    let relevantVariables = this.modelDict[selectedModelId]['relevant_variables'].split(',')
                    this.selectedModel=this.modelDict[selectedModelId]
                    this.fullVariableList=null
                    this.compPlots=null
                    if(relevantVariables&&relevantVariables.length>0&&relevantVariables[0]!==""){
                        let newVarList={};
                        for (let varName of relevantVariables){
                            let unit = this.getUnit(varName)
                            if (!newVarList[unit]) {
                                newVarList[unit] = [];
                            }
                            newVarList[unit].push({
                                'filters': {
                                    equipment_name: this.equipment,
                                    sensor_name: varName,
                                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                                },
                                seriesOverrides:{
                                    type:'scatter',
                                    opacity:1,
                                    xbins: {
                                        size: 1
                                    }
                                },
                            })
                        }
                        this.fullVariableList=newVarList
                        this.layoutOverrides={
                            title:this.$options.filters.translate("Relevant variables"),
                            xaxis: {
                                title: '',
                            },
                            yaxis:{
                                fixedrange: true
                            },
                            margin:{
                                t: 30,
                                b:0
                            },
                            height:300,
                            showlegend:true,
                            legend: {
                                orientation:'h',
                                y: -0.2
                            },
                            hoverlabel: {
                                namelength: -1
                            },
                            hovermode:"x",

                        }
                    }
                    if(relevantVariables.length===2){
                        this.compPlots=[{
                            id:254534369,
                            type: 'data3-chart',
                            config:{
                                "layout-overrides": {
                                    title:relevantVariables[0]+ " vs "+relevantVariables[1],
                                    xaxis: {
                                        title: relevantVariables[0]+"("+this.getUnit(relevantVariables[0])+")"
                                    },
                                    yaxis: {
                                        title: relevantVariables[1]+"("+this.getUnit(relevantVariables[1])+")"
                                    },
                                    height:300,
                                    showlegend:false,
                                    margin:{
                                        t: 30,
                                        b:0
                                    },
                                },
                                "x-axis": relevantVariables[0],
                                "y-axis": null,
                                "independent-x": false,
                                "color-axis": null,
                                "color-axis-scale": null,
                                "color-axis-title": null,
                                "data-payload": [{
                                    "filters": {
                                        equipment_name: this.equipment,
                                        facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                                        sensor_name: relevantVariables[0]
                                    },
                                    "seriesOverrides": {"type": "scatter",mode: "markers", "opacity": 1},
                                    "pipeline": []
                                }, {
                                    "filters": {
                                        equipment_name: this.equipment,
                                        facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                                        sensor_name: relevantVariables[1]
                                    },
                                    "seriesOverrides": {"type": "scatter", "opacity": 1, mode: "markers",},
                                    "pipeline": []
                                }]
                            }
                        }]
                    } else if(relevantVariables.length>2){
                        this.compPlots=[{
                            'id': 4646,
                            'type': 'data3-chart',
                            'config': {
                                "layout-overrides": {
                                    title:relevantVariables[0]+ " vs "+relevantVariables[1]+ " vs "+relevantVariables[2],
                                    "showlegend": false,
                                    "height": 800,
                                    "margin": {"l": 50, "r": 50, "t": 30, "pad": 1},
                                    "xaxis": {"title": {"text": relevantVariables[0]}},
                                    "yaxis": {"title": {"text": relevantVariables[1]}},
                                    "zaxis": {"title": {"text": relevantVariables[2]}},

                                },
                                "x-axis": relevantVariables[0],
                                "y-axis": relevantVariables[1],
                                "data-payload": [{
                                    "filters": {
                                        equipment_name: this.equipment,
                                        facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                                        sensor_name: relevantVariables[0]
                                    },
                                    "seriesOverrides": {"type": "scatter", "mode": "lines", "opacity": 1},
                                    "pipeline": []
                                }, {
                                    "filters": {
                                        equipment_name: this.equipment,
                                        facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                                        sensor_name: relevantVariables[1]
                                    },
                                    "seriesOverrides": {"type": "scatter", "opacity": 1, "mode": "lines"},
                                    "pipeline": []
                                }, {
                                    "filters": {
                                        equipment_name: this.equipment,
                                        facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                                        sensor_name: relevantVariables[2]
                                    },
                                    "seriesOverrides": {"type": "scatter3d", "opacity": 0.4, "mode": "markers"},
                                    "pipeline": []
                                }]
                            }
                        }]
                    }
                    this.showDetails=true
                }
            },
        },
        mounted() {
            this.refresh()
        }
    }
</script>

<style scoped>

</style>
